import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Edit, Plus, Trash2, Power, Brain, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = {
  id: '',
  name_ar: '',
  name_en: '',
  price: 0,
  currency: 'USD',
  duration_days: 30,
  sessions_included: 1,
  session_minutes: 30,
  billing_label_ar: 'شهريًا',
  billing_label_en: 'monthly',
  features_ar: [],
  features_en: [],
  popular: false,
  sort_order: 99,
  enabled: true,
};

export const AdminPsychologyPackagesView = () => {
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/psychology-packages`, { withCredentials: true });
      setPackages(res.data || []);
    } catch (_e) {
      toast.error(t('فشل تحميل الباقات', 'Failed to load packages'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...BLANK });
    setOpen(true);
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      ...BLANK,
      ...pkg,
      features_ar: pkg.features_ar || [],
      features_en: pkg.features_en || [],
    });
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFeatures = (k, text) => setForm((f) => ({ ...f, [k]: text.split('\n').map(s => s.trim()).filter(Boolean) }));

  const save = async () => {
    if (!form.name_ar && !form.name_en) {
      toast.error(t('الرجاء إدخال اسم الباقة', 'Please enter package name'));
      return;
    }
    if (!editingId && !form.id.trim()) {
      toast.error(t('الرجاء إدخال معرف الباقة', 'Please enter package id'));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, duration_days: Number(form.duration_days) || 0, sessions_included: Number(form.sessions_included) || 1, session_minutes: Number(form.session_minutes) || 30, sort_order: Number(form.sort_order) || 99 };
      if (editingId) {
        delete payload.id;
        await axios.put(`${API}/admin/psychology-packages/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/psychology-packages`, payload, { withCredentials: true });
      }
      toast.success(t('تم الحفظ', 'Saved'));
      setOpen(false);
      await fetchPackages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (pkg) => {
    try {
      await axios.put(`${API}/admin/psychology-packages/${pkg.id}`, { enabled: !pkg.enabled }, { withCredentials: true });
      await fetchPackages();
    } catch (_e) { toast.error('Failed'); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(t(`حذف باقة "${pkg.name_ar || pkg.name_en}"؟`, `Delete "${pkg.name_en || pkg.name_ar}"?`))) return;
    try {
      await axios.delete(`${API}/admin/psychology-packages/${pkg.id}`, { withCredentials: true });
      toast.success(t('تم الحذف', 'Deleted'));
      await fetchPackages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-psychology-packages-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xl font-bold text-[#2c3e50] flex items-center gap-2">
          <Brain size={20} /> {t('باقات الصحة النفسية', 'Psychology Packages')}
        </h3>
        <Button onClick={startCreate} className="bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="add-psy-package-button">
          <Plus size={16} className="mr-1" /> {t('باقة جديدة', 'New package')}
        </Button>
      </div>

      {loading && <div className="text-center text-gray-500 py-6">Loading…</div>}
      {!loading && packages.length === 0 && (
        <Card className="p-6 text-center text-gray-500">{t('لا توجد باقات', 'No packages')}</Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((pkg) => (
          <Card key={pkg.id} className={`p-4 relative ${pkg.enabled ? '' : 'opacity-60'} ${pkg.popular ? 'border-[#1e88e5] ring-1 ring-[#1e88e5]/30' : ''}`} data-testid={`psy-package-card-${pkg.id}`}>
            {pkg.popular && (
              <span className="absolute top-2 right-2 text-[10px] bg-[#1e88e5] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                <Sparkles size={10} /> Popular
              </span>
            )}
            <div className="text-xs text-gray-500 font-mono mb-1">{pkg.id}</div>
            <div className="font-bold text-[#0d2c54]">{pkg.name_ar || pkg.name_en}</div>
            <div className="text-sm text-gray-600 mb-2">{pkg.name_en}</div>
            <div className="text-2xl font-extrabold text-[#e91e63]">${pkg.price}</div>
            <div className="text-xs text-gray-500 mb-3">
              {pkg.currency} · {pkg.duration_days > 0 ? `${pkg.duration_days} days` : 'one-time'}
              {pkg.session_minutes ? ` · ${pkg.session_minutes} min/session` : ''}
              {pkg.billing_label_en ? ` · ${pkg.billing_label_en}` : ''}
            </div>
            {(pkg.features_ar || []).slice(0, 3).map((f, i) => (
              <div key={`${pkg.id}-feat-${i}`} className="text-xs text-gray-600 truncate">• {f}</div>
            ))}
            <div className="flex justify-between mt-3 pt-3 border-t">
              <Button size="sm" variant="ghost" onClick={() => toggleEnabled(pkg)} data-testid={`toggle-psy-${pkg.id}`}>
                <Power size={14} className={pkg.enabled ? 'text-green-600' : 'text-gray-400'} />
              </Button>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => startEdit(pkg)} data-testid={`edit-psy-${pkg.id}`}>
                  <Edit size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(pkg)} className="text-red-600 hover:text-red-700" data-testid={`delete-psy-${pkg.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="psy-package-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? t('تعديل الباقة', 'Edit Package') : t('باقة جديدة', 'New Package')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            {!editingId && (
              <div className="md:col-span-2">
                <Label>ID (unique) *</Label>
                <Input value={form.id} onChange={(e) => setField('id', e.target.value)} placeholder="e.g. psy_new_pkg" data-testid="psy-form-id" />
              </div>
            )}
            <div>
              <Label>{t('الاسم بالعربية', 'Name (Arabic)')} *</Label>
              <Input value={form.name_ar} onChange={(e) => setField('name_ar', e.target.value)} data-testid="psy-form-name-ar" />
            </div>
            <div>
              <Label>{t('الاسم بالإنجليزية', 'Name (English)')}</Label>
              <Input value={form.name_en} onChange={(e) => setField('name_en', e.target.value)} data-testid="psy-form-name-en" />
            </div>
            <div>
              <Label>{t('السعر', 'Price')} *</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setField('price', e.target.value)} data-testid="psy-form-price" />
            </div>
            <div>
              <Label>{t('العملة', 'Currency')}</Label>
              <Input value={form.currency} onChange={(e) => setField('currency', e.target.value)} data-testid="psy-form-currency" />
            </div>
            <div>
              <Label>{t('المدة (أيام)', 'Duration (days)')}</Label>
              <Input type="number" value={form.duration_days} onChange={(e) => setField('duration_days', e.target.value)} data-testid="psy-form-duration" />
              <p className="text-[11px] text-gray-500 mt-1">{t('0 = دفعة واحدة', '0 = one-time payment')}</p>
            </div>
            <div>
              <Label>{t('عدد الجلسات', 'Sessions included')}</Label>
              <Input type="number" value={form.sessions_included} onChange={(e) => setField('sessions_included', e.target.value)} data-testid="psy-form-sessions" />
            </div>
            <div>
              <Label>{t('مدة الجلسة (دقائق)', 'Session length (minutes)')}</Label>
              <Input type="number" value={form.session_minutes} onChange={(e) => setField('session_minutes', e.target.value)} data-testid="psy-form-session-minutes" />
              <p className="text-[11px] text-gray-500 mt-1">{t('يُستخدم للعدّاد داخل المكالمة', 'Drives the in-call countdown timer')}</p>
            </div>
            <div>
              <Label>{t('ترتيب العرض', 'Sort order')}</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} data-testid="psy-form-sort" />
            </div>
            <div>
              <Label>{t('التسمية عربي', 'Billing label (Arabic)')}</Label>
              <Input value={form.billing_label_ar} onChange={(e) => setField('billing_label_ar', e.target.value)} data-testid="psy-form-billing-ar" />
            </div>
            <div>
              <Label>{t('التسمية إنجليزي', 'Billing label (English)')}</Label>
              <Input value={form.billing_label_en} onChange={(e) => setField('billing_label_en', e.target.value)} data-testid="psy-form-billing-en" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('المميزات (عربي — سطر لكل ميزة)', 'Features (Arabic — one per line)')}</Label>
              <Textarea rows={4} value={(form.features_ar || []).join('\n')} onChange={(e) => setFeatures('features_ar', e.target.value)} data-testid="psy-form-features-ar" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('المميزات (إنجليزي — سطر لكل ميزة)', 'Features (English — one per line)')}</Label>
              <Textarea rows={4} value={(form.features_en || []).join('\n')} onChange={(e) => setFeatures('features_en', e.target.value)} data-testid="psy-form-features-en" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!form.popular} onCheckedChange={(v) => setField('popular', v)} data-testid="psy-form-popular" />
              <Label>{t('الأكثر طلباً', 'Most popular')}</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!form.enabled} onCheckedChange={(v) => setField('enabled', v)} data-testid="psy-form-enabled" />
              <Label>{t('مفعّلة', 'Enabled')}</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>{t('إلغاء', 'Cancel')}</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1e88e5]" data-testid="psy-form-save">
              {saving ? t('جاري...', 'Saving...') : t('حفظ', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPsychologyPackagesView;
